// Typed fetch client for the Agency-console endpoints (Phases 2–5). Same-origin
// `/api/*` (Vite proxies to the Worker in dev). Every request carries the local
// identity override (?as=agency) so the Worker scopes to the demo agency in dev.
import type {
  Plan,
  PlanContext,
  GatingRow,
  TicketMeta,
  Ticket,
  TicketDetailFull,
  AgencyUser,
  ClientAssignment,
  Analytics,
  Onboarding,
  AgencySettings,
  RequesterContext,
  TimeEntry,
  RunningTimer,
  TimeReport,
  TimeProvider,
  TierInfo,
  ProductTier,
  EntitySystemMap,
  EntityChange,
  EntityVersion,
  Announcement,
  RoutingRule,
  FeatureGroup,
  FeatureWithMatches,
  FeatureTicketMatch,
  SystemGraph,
  WorkflowRevision,
  WorkflowSummary,
  WorkflowRevisionDetail,
  SubAccount,
  AccountScan,
  AccountDocumentation,
  AssetDetail,
  AccountCharter,
  RiskStatus,
  SnapshotRecord,
} from './suite-types';
import { authHeaders, exchangeSession } from './session';

// The acting agent (from /api/me user.userId). Timers MUST be started and looked up as this
// identity — the agent drawer in the sub-account resolves the same SSO user, so a mismatch
// hides the console's running timer from the drawer (and Start there forks a second entry).
// null when SSO yielded no userId; timer UI must fail closed rather than invent an identity
// (a demo fallback here once attributed real timers to a seeded 'user-alex').
let activeAgentId: string | null = null;
export function setActiveAgentId(id: string | null): void { activeAgentId = id; }
export function actingAgentId(): string | null { return activeAgentId; }

// ── low-level helpers ──────────────────────────────────────────────
// The resolved agency id (from /api/me — SSO in prod, ?as override in dev). Every agency call
// must carry it, because the Worker's resolveAgencyId reads agencyId/companyId (NOT ?as) and
// otherwise defaults to the demo agency → the console would silently show the wrong data.
let activeAgencyId: string | null = null;
export function setActiveAgencyId(id: string | null): void { activeAgencyId = id; }

function scopeParams(): string {
  const parts: string[] = [];
  if (typeof window !== 'undefined') {
    const as = new URLSearchParams(window.location.search).get('as');
    if (as) parts.push(`as=${encodeURIComponent(as)}`);
  }
  if (activeAgencyId) parts.push(`agencyId=${encodeURIComponent(activeAgencyId)}`);
  return parts.join('&');
}

// Merge the resolved identity (agencyId + dev ?as) into a path that may already carry a query.
function withAs(path: string): string {
  const sp = scopeParams();
  if (!sp) return `/api${path}`;
  return `/api${path}${path.includes('?') ? '&' : '?'}${sp}`;
}

async function req<T>(method: string, path: string, body?: unknown, _retried = false): Promise<T> {
  const res = await fetch(withAs(path), {
    method,
    headers: {
      accept: 'application/json',
      ...authHeaders(),
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  // Token missing/expired → re-exchange the SSO blob once, then retry.
  if (res.status === 401 && !_retried) {
    await exchangeSession();
    return req<T>(method, path, body, true);
  }
  if (!res.ok) throw new Error(`${method} ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

const get = <T>(path: string) => req<T>('GET', path);
const put = <T>(path: string, body: unknown) => req<T>('PUT', path, body);
const post = <T>(path: string, body?: unknown) => req<T>('POST', path, body);
const patch = <T>(path: string, body: unknown) => req<T>('PATCH', path, body);
const patch_ = patch;
const del = <T>(path: string) => req<T>('DELETE', path);

// Multipart upload — bypasses `req` (which forces application/json). Must NOT set content-type;
// the browser adds the multipart boundary. Same scope + auth + one-retry-on-401 as `req`.
async function upload<T>(path: string, form: FormData, _retried = false): Promise<T> {
  const res = await fetch(withAs(path), { method: 'POST', headers: { accept: 'application/json', ...authHeaders() }, body: form });
  if (res.status === 401 && !_retried) { await exchangeSession(); return upload<T>(path, form, true); }
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

// ── response shapes (contract, PLAN.md "Phase 2–5 endpoint contract") ──
export interface TicketsMetaResponse {
  statuses: TicketMeta[];
  priorities: TicketMeta[];
  categories: TicketMeta[];
}
export interface NewTicketInput {
  subject: string;
  body: string;
  category_id?: string | null;
  context_area?: string | null;
  context_route?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  requester_user_id?: string | null;
  location_id?: string | null;
}
export interface TicketPatch {
  status_id?: string | null;
  priority_id?: string | null;
  category_id?: string | null;
  assigned_to?: string | null;
}
export interface NewCommentInput {
  body: string;
  author_type: 'agent' | 'customer' | 'system';
  author_name?: string | null;
  is_internal?: boolean;
  loom_url?: string | null;   // attach a Loom recording to this comment
}
export interface BrandingInput {
  whitelabelUrl?: string;
  logoUrl?: string;
  accent?: string;
  /** What their white-labeled HighLevel is called. */
  appName?: string;
  /** The agency entity/business name — a different noun from appName, used in different lines. */
  businessName?: string;
  /** Which HighLevel theme their users see, so captures match. */
  hlTheme?: 'light' | 'dark';
}
export interface VoiceInput {
  // base64-encoded sample(s) — the Worker forwards to ElevenLabs (graceful stub if unset)
  samples: string[];
  name?: string;
}
export interface TimeStartInput {
  agent_id: string;
  agent_name?: string | null;
  note?: string | null;
  billable?: boolean;
}
export interface TimeEntryPatch {
  billable?: boolean;
  note?: string | null;
  seconds?: number;
}
export interface AgencyUserPatch {
  rate_cents?: number | null;
  role?: AgencyUser['role'];
  active?: boolean;
}
// New draft announcement — audience defaults to 'all' on the Worker when omitted.
export interface NewAnnouncementInput {
  title: string;
  body?: string | null;
  audience?: Announcement['audience'];
  location_id?: string | null;
}
export interface TimeReportQuery {
  range?: string;
  groupBy?: TimeReport['groupBy'];
}

// Some Worker stubs wrap payloads inconsistently while parallel work is in flight.
// These unwrap helpers tolerate `{tickets:[…]}` | `[…]` and `{settings:{…}}` | `{…}`.
function arr<T>(v: unknown, key: string): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === 'object' && Array.isArray((v as Record<string, unknown>)[key])) {
    return (v as Record<string, T[]>)[key];
  }
  return [];
}

export const agencyApi = {
  // ── Plans (P2) ──
  async plansConfig(): Promise<Plan[]> {
    return arr<Plan>(await get<unknown>('/plans/config'), 'plans');
  },
  async savePlansConfig(plans: Array<{ id: string; offered: boolean }>): Promise<Plan[]> {
    return arr<Plan>(await put<unknown>('/plans/config', { plans }), 'plans');
  },
  syncPlans: () => post<{ count?: number; ok?: boolean }>('/plans/sync'),
  planContext: (locationId: string) =>
    get<PlanContext>(`/plans?locationId=${encodeURIComponent(locationId)}`),
  async gating(): Promise<GatingRow[]> {
    return arr<GatingRow>(await get<unknown>('/content/gating'), 'gating');
  },
  async saveGating(gating: GatingRow[]): Promise<GatingRow[]> {
    return arr<GatingRow>(await put<unknown>('/content/gating', { gating }), 'gating');
  },

  // ── Help desk (P3) ──
  ticketsMeta: () => get<TicketsMetaResponse>('/tickets/meta'),
  async tickets(params: { status?: string; assignee?: string; mine?: boolean } = {}): Promise<Ticket[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.assignee) qs.set('assignee', params.assignee);
    if (params.mine) qs.set('mine', '1');
    const q = qs.toString();
    return arr<Ticket>(await get<unknown>(`/tickets${q ? `?${q}` : ''}`), 'tickets');
  },
  createTicket: (input: NewTicketInput) => post<{ ok?: boolean; id?: string }>('/tickets', input),
  // Full agency ticket view: {ticket, comments, context, attachments, viewedContent}.
  ticket: (id: string) => get<TicketDetailFull>(`/tickets/${encodeURIComponent(id)}`),
  // Same-origin URL for an attachment's bytes (R2-proxied), dev identity param merged in.
  // Usable directly as an <img src> or download href.
  attachmentUrl: (ticketId: string, attId: string) =>
    withAs(`/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attId)}`),
  patchTicket: (id: string, patch: TicketPatch) =>
    req<{ ok?: boolean }>('PATCH', `/tickets/${encodeURIComponent(id)}`, patch),
  addComment: (id: string, input: NewCommentInput) =>
    post<{ ok?: boolean }>(`/tickets/${encodeURIComponent(id)}/comments`, input),
  async agencyUsers(): Promise<AgencyUser[]> {
    return arr<AgencyUser>(await get<unknown>('/agency-users'), 'users');
  },
  async assignments(): Promise<ClientAssignment[]> {
    return arr<ClientAssignment>(await get<unknown>('/assignments'), 'assignments');
  },
  async saveAssignments(assignments: ClientAssignment[]): Promise<ClientAssignment[]> {
    return arr<ClientAssignment>(await put<unknown>('/assignments', { assignments }), 'assignments');
  },
  patchAgencyUser: (id: string, p: AgencyUserPatch) =>
    patch<AgencyUser>(`/agency-users/${encodeURIComponent(id)}`, p),

  // ── Requester / sub-account context on the ticket view ──
  requester: (ticketId: string) =>
    get<RequesterContext>(`/tickets/${encodeURIComponent(ticketId)}/requester`),

  // ── Time tracking ──
  // Start a timer on a ticket (one running timer per agent — the Worker pauses any prior).
  timeStart: (ticketId: string, input: TimeStartInput) =>
    post<RunningTimer>(`/tickets/${encodeURIComponent(ticketId)}/time/start`, input),
  timePause: (entryId: string) => post<TimeEntry>(`/time/${encodeURIComponent(entryId)}/pause`),
  timeResume: (entryId: string) => post<TimeEntry>(`/time/${encodeURIComponent(entryId)}/resume`),
  timeStop: (entryId: string) => post<TimeEntry>(`/time/${encodeURIComponent(entryId)}/stop`),
  timePatch: (entryId: string, p: TimeEntryPatch) =>
    patch<TimeEntry>(`/time/${encodeURIComponent(entryId)}`, p),
  // The agent's single active timer (or null) — drawer + console restore it.
  timeRunning: (agentId: string) =>
    get<RunningTimer | null>(`/time/running?agentId=${encodeURIComponent(agentId)}`),
  async ticketTime(ticketId: string): Promise<TimeEntry[]> {
    return arr<TimeEntry>(await get<unknown>(`/tickets/${encodeURIComponent(ticketId)}/time`), 'entries');
  },
  timeReport: (q: TimeReportQuery = {}) => {
    const qs = new URLSearchParams();
    if (q.range) qs.set('range', q.range);
    if (q.groupBy) qs.set('groupBy', q.groupBy);
    const s = qs.toString();
    return get<TimeReport>(`/time/report${s ? `?${s}` : ''}`);
  },
  async timeProviders(): Promise<TimeProvider[]> {
    return arr<TimeProvider>(await get<unknown>('/time/providers'), 'providers');
  },
  async saveTimeProviders(providers: TimeProvider[]): Promise<TimeProvider[]> {
    return arr<TimeProvider>(await put<unknown>('/time/providers', { providers }), 'providers');
  },

  // ── Analytics (P4) ──
  analytics: (range = '30d') => get<Analytics>(`/analytics?range=${encodeURIComponent(range)}`),

  // ── Onboarding + settings (P5) ──
  onboarding: () => get<Onboarding>('/onboarding'),
  saveOnboarding: (patch: Partial<Onboarding> & { step?: number }) =>
    put<Onboarding>('/onboarding', patch),
  cloneBranding: (input: BrandingInput) => post<Onboarding>('/onboarding/branding', input),
  cloneVoice: (input: VoiceInput) => post<Onboarding>('/onboarding/voice', input),
  settings: async (): Promise<AgencySettings | null> => {
    const v = await get<unknown>('/settings');
    if (v && typeof v === 'object' && 'settings' in v) {
      return (v as { settings: AgencySettings | null }).settings;
    }
    return v as AgencySettings | null;
  },
  // Main save: widget + delivery fields only. Voice/meta are stripped so a routine save never
  // triggers ElevenLabs validation or the Pro-plan voice gate.
  saveSettings: (s: AgencySettings) => {
    const { elevenlabs_voice_id, elevenlabs_key_set, voice_allowed, common_voice_id, product_tier, ...rest } =
      s as AgencySettings & { entitlements?: unknown };
    void elevenlabs_voice_id; void elevenlabs_key_set; void voice_allowed; void common_voice_id; void product_tier;
    return put<{ ok?: boolean }>('/settings', rest);
  },
  // Console look — its own tiny save so choosing a theme never round-trips the whole settings form.
  saveTheme: (agency_theme: string) => put<{ ok?: boolean }>('/settings', { agency_theme }),
  // Branded videos (whiten) — activation scope + the record/revoice fan-out (self-scoped server-side).
  whitenEstimate: () => get<WhitenEstimate>('/whiten/estimate'),
  whiten: (mode: 'record' | 'revoice') => post<{ ok: boolean; mode: string; requeued: number; created: number }>('/whiten', { mode }),

  // Voice (BYO ElevenLabs) — its OWN save. Send elevenlabs_key only when the user entered/changed
  // it; send "" to clear the stored key. Returns the settings shape (with elevenlabs_key_set).
  saveVoice: (body: { elevenlabs_key?: string; elevenlabs_voice_id?: string | null }) =>
    put<AgencySettings>('/settings', body),

  // ── Product tier (what the agency bought — gates which suite features they get) ──
  getTier: () => get<TierInfo>('/tier'),
  setTier: (tier: ProductTier) => patch<TierInfo>('/tier', { product_tier: tier }),

  // ── Change intelligence (Pro+) ──
  // Per-sub-account entity inventory (counts by type + entities w/ change_count/last_change_at).
  ciEntities: (locationId: string) =>
    get<EntitySystemMap>(`/ci/entities?locationId=${encodeURIComponent(locationId)}`),
  // Re-inventory from HighLevel (upserts ss_sa_entities). Idempotent; graceful when no OAuth token.
  ciIngest: (locationId: string) =>
    post<EntitySystemMap>('/ci/ingest', { locationId }),
  // Change feed (who changed what, when) — optionally scoped to a sub-account and/or one entity.
  async ciChanges(params: { locationId?: string; entityId?: string } = {}): Promise<EntityChange[]> {
    const qs = new URLSearchParams();
    if (params.locationId) qs.set('locationId', params.locationId);
    if (params.entityId) qs.set('entityId', params.entityId);
    const q = qs.toString();
    return arr<EntityChange>(await get<unknown>(`/ci/changes${q ? `?${q}` : ''}`), 'changes');
  },
  // Version timeline for one captured entity (by its ss_sa_entities primary key).
  async ciVersions(entityPk: string): Promise<EntityVersion[]> {
    return arr<EntityVersion>(
      await get<unknown>(`/ci/entity/${encodeURIComponent(entityPk)}/versions`),
      'versions',
    );
  },

  // ── Smart routing (prioritized rules) ──
  // List routing rules in priority order (lowest priority number = evaluated first / wins).
  async routingRules(): Promise<RoutingRule[]> {
    return arr<RoutingRule>(await get<unknown>('/routing-rules'), 'rules');
  },
  // Replace the full rule set (used for add / delete / reorder / toggle in one PUT).
  async saveRoutingRules(rules: RoutingRule[]): Promise<RoutingRule[]> {
    return arr<RoutingRule>(await put<unknown>('/routing-rules', { rules }), 'rules');
  },
  // Feature-group → features taxonomy (derived from the how-level feature tree).
  async taxonomy(): Promise<FeatureGroup[]> {
    return arr<FeatureGroup>(await get<unknown>('/taxonomy'), 'groups');
  },

  // ── Changelog intelligence + ticket matching (Radar) ──
  // Watched features (roadmap/labs/released/known_issue) each with their suggested ticket matches.
  async features(): Promise<FeatureWithMatches[]> {
    return arr<FeatureWithMatches>(await get<unknown>('/features'), 'features');
  },
  // Pull changelog / whats-new into ss_feature_watch. Idempotent (insert-if-absent).
  ingestFeatures: () => post<{ ingested?: number; ok?: boolean }>('/features/ingest'),
  // Score released features against past tickets → insert `suggested` matches.
  matchFeatures: () => post<{ matched?: number; ok?: boolean }>('/features/match'),
  // Dismiss or mark-notified a single feature↔ticket match.
  patchMatch: (id: string, status: FeatureTicketMatch['status']) =>
    patch<FeatureTicketMatch>(`/matches/${encodeURIComponent(id)}`, { status }),

  // ── Doc-intel graph (System Map: connections + health) ──
  // Full relationship graph for a sub-account: nodes (entities) + edges (relations) + health checks.
  ciGraph: (locationId: string) =>
    get<SystemGraph>(`/ci/graph?locationId=${encodeURIComponent(locationId)}`),
  // Parse a GHL snapshot / location-export JSON → upsert entities + derive relation edges.
  ciImport: (json: unknown) => post<SystemGraph>('/ci/import', json),
  // Derive ss_entity_health (missing_doc, broken_ref, orphaned, unpublished, no_recent_change).
  ciAnalyze: (locationId: string) => post<SystemGraph>('/ci/analyze', { locationId }),

  // ── Workflow change-log (app-side capture → revisions + diffs) ──
  // Recent changed revisions. Scoped to one sub-account when locationId is given (the ticket
  // panel), else agency-wide (withAs carries agencyId → the console's Workflow Changes feed).
  async workflowChangelog(params: { locationId?: string; limit?: number } = {}): Promise<WorkflowRevision[]> {
    const qs = new URLSearchParams();
    if (params.locationId) qs.set('locationId', params.locationId);
    if (params.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return arr<WorkflowRevision>(await get<unknown>(`/workflows/changelog${q ? `?${q}` : ''}`), 'changelog');
  },
  // One row per tracked workflow (latest revision + history counts), agency-wide.
  async workflows(): Promise<WorkflowSummary[]> {
    return arr<WorkflowSummary>(await get<unknown>('/workflows'), 'workflows');
  },
  // Full revision history for one workflow (most recent first, baselines included).
  async workflowRevisions(locationId: string, workflowId: string): Promise<WorkflowRevision[]> {
    return arr<WorkflowRevision>(
      await get<unknown>(`/workflows/revisions?locationId=${encodeURIComponent(locationId)}&workflowId=${encodeURIComponent(workflowId)}`),
      'revisions',
    );
  },
  // One revision with steps + the prior revision's steps (before/after diff view).
  workflowRevision: (id: string) =>
    get<WorkflowRevisionDetail>(`/workflows/revisions/${encodeURIComponent(id)}`),

  // ── Announcements ──
  async announcements(): Promise<Announcement[]> {
    return arr<Announcement>(await get<unknown>('/announcements'), 'announcements');
  },
  createAnnouncement: (a: NewAnnouncementInput) => post<Announcement>('/announcements', a),
  publishAnnouncement: (id: string) =>
    patch<Announcement>(`/announcements/${encodeURIComponent(id)}`, { status: 'published' }),

  // ── Content Studio — bring-your-own videos ──
  async studioAssets(): Promise<StudioAsset[]> {
    return arr<StudioAsset>(await get<unknown>('/studio/assets'), 'assets');
  },
  async studioVideos(assetId?: string): Promise<StudioVideo[]> {
    return arr<StudioVideo>(await get<unknown>(`/studio/videos${assetId ? `?asset_id=${encodeURIComponent(assetId)}` : ''}`), 'videos');
  },
  attachStudioVideo: (input: { asset_id: string; mode: 'add' | 'replace'; url: string; title?: string }) =>
    post<{ ok: boolean; id: string }>('/studio/videos', input),
  uploadStudioVideo: (input: { asset_id: string; mode: 'add' | 'replace'; file: File; title?: string }) => {
    const fd = new FormData();
    fd.append('asset_id', input.asset_id);
    fd.append('mode', input.mode);
    if (input.title) fd.append('title', input.title);
    fd.append('file', input.file);
    return upload<{ ok: boolean; id: string }>('/studio/videos', fd);
  },
  patchStudioVideo: (id: string, p: { mode?: 'add' | 'replace'; title?: string }) =>
    patch<{ ok: boolean }>(`/studio/videos/${encodeURIComponent(id)}`, p),
  deleteStudioVideo: (id: string) => del<{ ok: boolean }>(`/studio/videos/${encodeURIComponent(id)}`),

  // FTUX welcome sequence
  async studioCatalog(): Promise<CatalogItem[]> {
    return arr<CatalogItem>(await get<unknown>('/studio/catalog'), 'assets');
  },
  async ftuxSequence(): Promise<FtuxItem[]> {
    return arr<FtuxItem>(await get<unknown>('/studio/ftux'), 'items');
  },
  saveFtux: (assetIds: string[]) => put<{ ok: boolean; count: number }>('/studio/ftux', { asset_ids: assetIds }),

  // Tour step-text overrides
  tourOverlay: (tour: string) =>
    get<{ tour: string; steps: TourOverride; state: string | null }>(`/studio/tour-overlay?tour=${encodeURIComponent(tour)}`),
  saveTourOverlay: (tour: string, steps: TourOverride) =>
    put<{ ok: boolean; steps: TourOverride }>('/studio/tour-overlay', { tour, steps }),

  // Doc notes (a Markdown note prepended to one of our articles)
  docNote: (slug: string) => get<{ slug: string; note: string }>(`/studio/doc-note?slug=${encodeURIComponent(slug)}`),
  saveDocNote: (slug: string, note: string) => put<{ ok: boolean; note: string }>('/studio/doc-note', { slug, note }),

  // My Content — net-new agency-authored guides (docs + videos)
  studioAreas: async (): Promise<AreaRef[]> => arr<AreaRef>(await get<unknown>('/studio/areas'), 'areas'),
  myContent: async (): Promise<MyContentItem[]> => arr<MyContentItem>(await get<unknown>('/studio/content'), 'content'),
  createDoc: (input: { title: string; area: string; body: string }) =>
    post<{ ok: boolean; id: string }>('/studio/content', { kind: 'doc', ...input }),
  createVideoEmbed: (input: { title: string; area: string; url: string; videoTitle?: string }) =>
    post<{ ok: boolean; id: string }>('/studio/content', { kind: 'video', ...input }),
  createVideoUpload: (input: { title: string; area: string; file: File; videoTitle?: string }) => {
    const fd = new FormData();
    fd.set('kind', 'video'); fd.set('title', input.title); fd.set('area', input.area); fd.set('file', input.file);
    if (input.videoTitle) fd.set('videoTitle', input.videoTitle);
    return upload<{ ok: boolean; id: string }>('/studio/content', fd);
  },
  updateContent: (id: string, input: { title?: string; area?: string; body?: string }) =>
    patch<{ ok: boolean }>(`/studio/content/${encodeURIComponent(id)}`, input),
  replaceVideoEmbed: (id: string, url: string, videoTitle?: string) =>
    post<{ ok: boolean }>(`/studio/content/${encodeURIComponent(id)}/video`, { url, videoTitle }),
  replaceVideoUpload: (id: string, file: File, videoTitle?: string) => {
    const fd = new FormData();
    fd.set('file', file); if (videoTitle) fd.set('videoTitle', videoTitle);
    return upload<{ ok: boolean }>(`/studio/content/${encodeURIComponent(id)}/video`, fd);
  },
  deleteContent: (id: string) => del<{ ok: boolean }>(`/studio/content/${encodeURIComponent(id)}`),

  // Review Center — reconcile queue for content we republished over the agency's customizations.
  reviewQueue: async (): Promise<ReviewItem[]> => arr<ReviewItem>(await get<unknown>('/review'), 'items'),
  reviewCount: () => get<{ count: number }>('/review/count'),
  resolveReview: (id: string, action: 'keep' | 'update' | 'discard', payload?: unknown) =>
    post<{ ok: boolean; action: string }>(`/review/${encodeURIComponent(id)}/resolve`, { action, payload }),

  // Inbox — agency notifications + per-user, per-type channel prefs.
  inbox: (opts?: { unread?: boolean; limit?: number }) =>
    get<{ unread: number; notifications: InboxNotification[] }>(
      `/inbox?${opts?.unread ? 'unread=1&' : ''}limit=${opts?.limit ?? 50}`,
    ),
  markRead: (ids?: string[]) => post<{ ok: boolean }>('/inbox/read', ids ? { ids } : {}),
  inboxPrefs: () => get<{ prefs: NotificationPref[] }>('/inbox/prefs'),
  saveInboxPrefs: (prefs: Array<{ type: string; inapp: boolean; email: boolean }>) =>
    put<{ prefs: NotificationPref[] }>('/inbox/prefs', { prefs }),

  // ── Sub-account registry + widget control (2026-07-31) ──
  // The widget ships via agency-wide Custom JS, so per-sub-account enablement lives server-side.
  async accounts(): Promise<SubAccount[]> {
    return arr<SubAccount>(await get<unknown>('/accounts'), 'accounts');
  },
  classifyAccount: (locationId: string, patch: { account_class?: string | null; account_purpose?: string | null }) =>
    patch_<{ location_id: string }>(`/accounts/${encodeURIComponent(locationId)}`, patch),
  suppressRule: (locationId: string, ruleId: string, reason?: string, undo?: boolean) =>
    post<{ ok: boolean; suppressed: boolean }>('/scan/suppress', { locationId, ruleId, reason, undo }),
  setAccountWidget: (locationId: string, enabled: boolean) =>
    patch<{ location_id: string; widget_enabled: boolean }>(
      `/accounts/${encodeURIComponent(locationId)}`, { widget_enabled: enabled },
    ),
  setWidgetBulk: (enabled: boolean, locationIds?: string[]) =>
    post<{ updated: number; enabled: boolean }>('/accounts/widget-bulk',
      locationIds?.length ? { enabled, location_ids: locationIds } : { enabled }),
  syncAccounts: () => post<{ added: number; named: number; total: number; pages: number; error: string | null; note?: string }>('/accounts/sync'),

  // ── Account documentation ──
  runScan: (locationId: string) => post<AccountScan & { changed: number }>('/scan/run', { locationId }),
  documentation: (locationId: string) =>
    get<AccountDocumentation>(`/scan/documentation?locationId=${encodeURIComponent(locationId)}`),
  assetDetail: (entityPk: string) =>
    get<AssetDetail & { definition_available: boolean }>(`/scan/asset/${encodeURIComponent(entityPk)}`),
  setRiskStatus: (id: string, status: RiskStatus, note?: string) =>
    patch<{ ok: boolean }>(`/scan/risk/${encodeURIComponent(id)}`, { status, ...(note ? { note } : {}) }),
  reaudit: (locationId: string) => post<{ ok: boolean; findings: number }>('/scan/reaudit', { locationId }),
  collectors: () => get<Array<{ id: string; label: string; lane: 'api' | 'app'; kind: string; verified: boolean; note: string | null }>>('/scan/collectors'),
  // ── PatchyHub export (proof of concept — simulated delivery only) ──
  patchyPlan: (locationId: string) =>
    get<PatchyPlan>(`/patchyhub/plan?locationId=${encodeURIComponent(locationId)}`),
  patchySync: (locationId: string, systemName: string) =>
    post<PatchyRunResult>('/patchyhub/sync', { locationId, systemName }),
  patchyRuns: (locationId?: string) =>
    get<{ runs: PatchyRun[] }>(`/patchyhub/runs${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`)
      .then((r) => r.runs ?? []),
  patchyDiagnostic: (runId: string) =>
    get<Record<string, unknown>>(`/patchyhub/diagnostic/${encodeURIComponent(runId)}`),

  charter: (locationId: string) =>
    get<AccountCharter>(`/accounts/${encodeURIComponent(locationId)}/charter`),
  saveCharter: (locationId: string, charter: Partial<AccountCharter>) =>
    put<AccountCharter>(`/accounts/${encodeURIComponent(locationId)}/charter`, charter),

  // ── Snapshot fleet ──
  async snapshots(): Promise<SnapshotRecord[]> {
    return arr<SnapshotRecord>(await get<unknown>('/snapshots'), 'snapshots');
  },
  createSnapshot: (body: { name: string; source_location_id: string; version_label?: string; notes?: string }) =>
    post<{ id: string; name: string; asset_count: number }>('/snapshots', body),
  checkSnapshot: (id: string, locationIds?: string[]) =>
    post<{ checked: number }>(`/snapshots/${encodeURIComponent(id)}/check`, locationIds?.length ? { location_ids: locationIds } : {}),
  deleteSnapshot: (id: string) => del<{ ok: boolean }>(`/snapshots/${encodeURIComponent(id)}`),
};

export type TourOverride = Record<string, { title?: string; description?: string }>;

export interface StudioAsset { id: string; title: string; type: string; flow_id: string | null; area: string | null; byo_count: number }
export interface CatalogItem { id: string; title: string; kind: 'doc' | 'video' | 'tour'; flow_id: string | null; doc_slug: string | null; area: string | null }
export interface FtuxItem { asset_id: string; position: number; title: string; kind: 'doc' | 'video' | 'tour'; flow_id: string | null; doc_slug: string | null; area: string | null }
export interface AreaRef { id: string; label: string }
export interface InboxNotification { id: string; type: string; title: string; body: string | null; ref: Record<string, unknown> | null; created_at: string; read: boolean }
export interface NotificationPref { type: string; label: string; inapp: boolean; email: boolean }
export interface ReviewItem {
  id: string; asset_id: string; content_type: 'doc' | 'tour'; asset_title: string;
  doc_slug: string | null; payload: string; base_master_version: string;
  new_version: string | null; updated_at: string; article_html: string | null;
}
export interface WhitenEstimate {
  active: boolean; entitled: boolean;
  recordTargets: number; revoiceTargets: number;
  ready: number; inProgress: number; failed: number;
}
export interface MyContentItem {
  id: string; title: string; type: string; kind: 'doc' | 'video';
  doc_slug: string | null; feature_id: string | null; area: string | null;
  markdown: string | null; video_source: 'upload' | 'embed' | null; video_ref: string | null; video_title: string | null;
  created_at: string;
}
export interface StudioVideo { id: string; asset_id: string; mode: 'add' | 'replace'; source: 'upload' | 'embed'; media_ref: string; title: string | null; sort_order: number; created_at: string; asset_title: string; flow_id: string | null }

// ── PatchyHub export types ──
// `sent` is carried explicitly on every result: round 1 builds a real payload from documented
// entities but never transmits it, and a caller must be able to tell those two states apart.
export interface PatchyTypeCount {
  tssType: string; patchyType: string; count: number; expansion: boolean;
}
export interface PatchyPlan {
  location_id: string;
  documented: boolean;
  documented_at: string | null;
  by_type: PatchyTypeCount[];
  dropped: Array<{ tssType: string; count: number }>;
  unnamed: number;
  totals: { entities: number; exportable: number; dropped: number };
  batches: Array<{ asset_type: string; count: number }>;
  sample: Record<string, unknown> | null;
  endpoint: string;
}
export interface PatchyRunResult {
  run_id: string; mode: 'simulated' | 'live'; sent: boolean;
  totals: { entities: number; exportable: number; dropped: number };
  by_type: PatchyTypeCount[];
  dropped: Array<{ tssType: string; count: number }>;
  batches: Array<{ asset_type: string; count: number }>;
}
export interface PatchyRun {
  id: string; location_id: string; system_name: string | null;
  mode: 'simulated' | 'live'; status: string;
  scanned: number; created: number; updated: number; failed: number; skipped: number;
  by_type: PatchyTypeCount[]; dropped: Array<{ tssType: string; count: number }>;
  created_at: string;
}
